import React from 'react';
import { Input, InputProps } from './Input';
import classNames from 'classnames';

export type BaseFieldProps = {
  containerClassName?: string;
  error?: React.ReactNode;
  hint?: React.ReactNode;
  label?: React.ReactNode;
  labelClassName?: string;
};

export type FieldProps = Omit<InputProps, 'error'> & BaseFieldProps;

interface FieldLabelProps {
  htmlFor?: string;
  id?: string;
  className?: string;
  error?: React.ReactNode | boolean;
  name?: string;
}

export const Label: React.FC<FieldLabelProps> = ({
  htmlFor,
  id,
  name,
  children,
  className,
  error,
}) => (
  <label
    className={classNames(
      'block text-sm pb-1 font-semibold',
      error && 'text-red-500',
      className,
    )}
    htmlFor={htmlFor || id || name}
  >
    {children}
  </label>
);

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      children,
      containerClassName,
      error,
      hint,
      id: baseId,
      label,
      labelClassName,
      name,
      ...props
    },
    ref,
  ) => {
    const id = baseId || name;
    return (
      <div className={classNames('mb-4', containerClassName)}>
        {label && (
          <Label
            htmlFor={id}
            name={name}
            error={error}
            className={labelClassName}
          >
            {label}
          </Label>
        )}

        {React.Children.count(children) > 0 ? (
          React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                ...child.props,
                error,
              });
            }
          })
        ) : (
          <Input
            error={!!error}
            id={id}
            name={name}
            {...props}
            aria-describedby={
              error ? `${id}-error` : hint ? `${id}-hint` : undefined
            }
            ref={ref}
          />
        )}

        {hint && (
          <div className="mt-1 text-xs text-slate-green-500" id={`${id}-hint`}>
            {hint}
          </div>
        )}

        {error && (
          <div className="mt-1 text-xs text-red-500" id={`${id}-error`}>
            {error}
          </div>
        )}
      </div>
    );
  },
);
